/**
 * Google sign-in for the embedded panel, run in a window we open ourselves.
 *
 * The panel is an iframe in web.whatsapp.com, which serves
 * Cross-Origin-Embedder-Policy: require-corp. To stay embeddable there the
 * panel must send COEP as well, and that blocks the hidden /__/auth/iframe
 * helper Firebase relies on to receive the result of BOTH signInWithPopup and
 * signInWithRedirect. The popup itself was never the problem — the result
 * simply never arrived, and the user was bounced back to the sign-in screen.
 *
 * So we skip Firebase's helper entirely: open our own /extension-login in a
 * window, let it talk to Google directly, and take the token back over
 * window.opener. It is same-origin with us, so both the origin and the exact
 * window can be checked — nothing in the host page can answer in its place.
 * The token is then exchanged with signInWithCredential, a REST call that
 * works fine under COEP.
 *
 * Scoped to the COEP routes only. The CRM and helpdesk embeds do not carry
 * COEP, so Firebase's popup still works there and is left untouched.
 */

/**
 * Origins whose /extension-login is registered as an authorized redirect URI
 * on the OAuth client. The same build is served from several hosting sites
 * (app.ulgebra.com, *.web.app, preview channels), and Google matches the
 * redirect URI exactly — so anywhere else this flow would fail at Google with
 * redirect_uri_mismatch and could not recover, losing a sign-in that works
 * today. Those surfaces are not embedded under COEP anyway, so the ordinary
 * popup still serves them. Add an origin here only once its /extension-login
 * is actually registered.
 */
const REGISTERED_ORIGINS = new Set([
  'https://sms.ulgebra.com',
  'http://localhost:3000',
]);

/**
 * Routes that actually need this flow. firebase.json serves them with
 * COEP: require-corp so they can be embedded in WhatsApp Web, and COEP is
 * exactly what breaks Firebase's own popup — everywhere else signInWithPopup
 * still works and is left alone.
 *
 * firebase.json also sets COEP on /ai-support-agent-extension/iframe*, but
 * that is a static page under public/, not a route this app renders, so it
 * never reaches this code and is deliberately not listed. Add a route here
 * only if the React app actually serves it.
 */
const COEP_ROUTES = [/^\/lookupframe/];

/**
 * Whether this page should use the window flow.
 *
 * Deliberately narrow on both axes: the origin must have /extension-login
 * registered with Google, and the route must be one that COEP actually
 * breaks. sms.ulgebra.com also serves the AppCursor /app widget and the CRM
 * and helpdesk panels, and those sign in perfectly well with the ordinary
 * popup today — there is no reason to move them onto a different mechanism.
 */
export function isSignInWindowSupported(): boolean {
  if (!REGISTERED_ORIGINS.has(window.location.origin)) return false;
  return COEP_ROUTES.some((re) => re.test(window.location.pathname));
}

const RESULT = 'ua-google-signin-result';
const WINDOW_NAME = 'ua-google-signin';
const WIDTH = 500;
const HEIGHT = 640;

/**
 * This origin or route does not use the window flow. Not exported: callers
 * decide with isSignInWindowSupported, and treat any throw from here the same
 * way — the class exists so a throw describes itself in a stack trace.
 */
class SignInWindowUnsupportedError extends Error {
  constructor() {
    super('Sign-in windows are not configured for this origin.');
    this.name = 'SignInWindowUnsupportedError';
  }
}

/** The browser refused the window. Not exported, for the reason above. */
class SignInWindowBlockedError extends Error {
  constructor() {
    super('The browser blocked the sign-in window.');
    this.name = 'SignInWindowBlockedError';
  }
}

/**
 * Open the sign-in window and resolve with a Google access token.
 *
 * MUST be called synchronously from a click handler. window.open needs the
 * click's transient activation, and awaiting anything first spends it — the
 * window is then blocked outright.
 */
export function signInWithGoogleWindow(): Promise<string> {
  if (!isSignInWindowSupported()) throw new SignInWindowUnsupportedError();

  const requestId = newRequestId();
  const url = `${window.location.origin}/extension-login?rid=${encodeURIComponent(requestId)}`;

  // Centre on the screen the browser is on, not the panel, which is a narrow
  // strip at the edge of the page.
  const left = Math.round(window.screenX + (window.outerWidth - WIDTH) / 2);
  const top = Math.round(window.screenY + (window.outerHeight - HEIGHT) / 2);
  const features = `popup=1,width=${WIDTH},height=${HEIGHT},left=${left},top=${top}`;

  const win = window.open(url, WINDOW_NAME, features);
  // Thrown rather than rejected on purpose: the caller learns synchronously,
  // while what is left of the click's activation can still be spent on a
  // fallback. A rejection would only surface an await later, by which time it
  // is certainly gone.
  if (!win) throw new SignInWindowBlockedError();

  return new Promise<string>((resolve, reject) => {
    let settled = false;

    const done = (fn: () => void) => {
      if (settled) return;
      settled = true;
      window.removeEventListener('message', onMessage);
      clearInterval(closedPoll);
      try {
        win.close();
      } catch {
        /* already gone */
      }
      fn();
    };

    const onMessage = (event: MessageEvent) => {
      // Same-origin and from the exact window we opened. The host page cannot
      // satisfy either, so it can neither read the token nor supply one.
      if (event.origin !== window.location.origin) return;
      if (event.source !== win) return;

      const msg = event.data;
      if (!msg || typeof msg !== 'object') return;
      if (msg.type !== RESULT || msg.requestId !== requestId) return;

      if (typeof msg.accessToken === 'string' && msg.accessToken) {
        const token = msg.accessToken;
        done(() => resolve(token));
      } else {
        done(() => reject(new Error('Google sign-in did not complete. Please try again.')));
      }
    };

    // No timeout: the user may take as long as they like at Google. Closing the
    // window is the cancel signal, and there is no event for that on a
    // cross-origin window — polling is the only way to notice.
    const closedPoll = setInterval(() => {
      if (win.closed) done(() => reject(new Error('Sign-in was cancelled.')));
    }, 500);

    window.addEventListener('message', onMessage);
  });
}

function newRequestId(): string {
  try {
    return crypto.randomUUID();
  } catch {
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }
}
